/**
 * Resolves once the OL map is in a stable, fully-loaded state — every visible
 * tile/image source has finished its current load (`loadend` seen with no
 * subsequent `loadstart`) AND the compositor has painted a full frame
 * (`rendercomplete`). A safety ceiling guarantees the export never hangs on a
 * permanently-stuck WMS tile.
 *
 * Returns `'stable' | 'timeout'` so callers can log when the safety net trips.
 *
 * Why we cannot rely on `rendercomplete` alone: OL fires it on every paint,
 * including paints that show a low-resolution preview while the
 * higher-resolution tile request is still in flight. From the QV
 * download-from-history flow the dialog opens within ~1s of the hidden maps
 * mounting, and capturing on the first `rendercomplete` ships a frame with
 * missing layers / blurry tiles. The Map's aggregate `loadstart`/`loadend`
 * events (OL ≥6.14) reflect actual tile-source state and are the right gate.
 */
export const waitForMapLoaded = (
  olMap,
  { timeoutMs = 8000, quiescenceMs = 100 } = {},
) => {
  return new Promise(resolve => {
    let isLoading = false
    let renderCompleted = false
    let resolved = false
    let stableTimer = null

    const onLoadStart = () => {
      isLoading = true
      if (stableTimer) {
        clearTimeout(stableTimer)
        stableTimer = null
      }
    }
    const onLoadEnd = () => {
      isLoading = false
      checkStable()
    }
    const checkStable = () => {
      if (resolved || isLoading || !renderCompleted) return
      if (stableTimer) clearTimeout(stableTimer)
      stableTimer = setTimeout(() => finish('stable'), quiescenceMs)
    }
    const finish = reason => {
      if (resolved) return
      resolved = true
      olMap.un('loadstart', onLoadStart)
      olMap.un('loadend', onLoadEnd)
      if (stableTimer) clearTimeout(stableTimer)
      clearTimeout(safety)
      resolve(reason)
    }
    const safety = setTimeout(() => finish('timeout'), timeoutMs)

    olMap.on('loadstart', onLoadStart)
    olMap.on('loadend', onLoadEnd)
    olMap.once('rendercomplete', () => {
      renderCompleted = true
      checkStable()
    })
    olMap.renderSync()
  })
}

export const drawMap = map => {
  const size = map.getSize()
  if (!size || !size[0] || !size[1]) {
    console.warn('[ExportService] map.getSize() invalid; skipping drawMap', { size })
    return null
  }
  const canvases = map
    .getViewport()
    .querySelectorAll('.ol-layer canvas, canvas.ol-layer')
  const mapCanvas = document.createElement('canvas')
  mapCanvas.width = size[0]
  mapCanvas.height = size[1]
  const mapContext = mapCanvas.getContext('2d')
  canvases.forEach(canvas => {
    if (canvas.width > 0) {
      const opacity = canvas.parentNode.style.opacity || canvas.style.opacity
      mapContext.globalAlpha = opacity === '' ? 1 : Number(opacity)
      let matrix
      const transform = canvas.style.transform
      if (transform) {
        // Get the transform parameters from the style's transform matrix
        matrix = transform
          .match(/^matrix\(([^(]*)\)$/)[1]
          .split(',')
          .map(Number)
      } else {
        matrix = [
          Number.parseFloat(canvas.style.width) / canvas.width,
          0,
          0,
          Number.parseFloat(canvas.style.height) / canvas.height,
          0,
          0,
        ]
      }
      // Apply the transform to the export map context
      CanvasRenderingContext2D.prototype.setTransform.apply(mapContext, matrix)
      const backgroundColor = canvas.parentNode.style.backgroundColor
      if (backgroundColor) {
        mapContext.fillStyle = backgroundColor
        mapContext.fillRect(0, 0, canvas.width, canvas.height)
      }
      mapContext.drawImage(canvas, 0, 0)
    }
  })

  mapContext.globalAlpha = 1
  mapContext.setTransform(1, 0, 0, 1, 0, 0)

  return mapCanvas
}
